import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchCurrentSession } from '../lib/session';
import type { User, LoginCredentials, RegisterData } from '../types';

// ---------------------------
// Context shape
// ---------------------------

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<User>;
  loginWithGoogle: (credential: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ---------------------------
// Hook
// ---------------------------

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return context;
};

// ---------------------------
// Provider
// ---------------------------

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async (): Promise<User | null> => {
    try {
      const data = await fetchCurrentSession();
      if (data && data.user) {
        setUser(data.user);
        return data.user;
      }
      setUser(null);
      return null;
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      // 401/403 = session réellement invalide → on déconnecte (pas de fantôme).
      // Sans statut HTTP (réseau coupé, timeout) → on préserve l'utilisateur.
      if (status === 401 || status === 403) {
        setUser(null);
        return null;
      }
      if (status !== undefined) {
        // Autre erreur serveur : on préserve mais on ne fait plus confiance aveuglément.
        console.warn("Erreur serveur lors du rafraîchissement utilisateur:", err);
        return user;
      }
      console.warn("Erreur transitoire lors du rafraîchissement utilisateur:", err);
      return user;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await refreshUser();
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    const { api } = await import('../lib/api');
    const data = await api.auth.login(credentials);
    setUser(data.user);
    return data.user;
  };

  const register = async (data: RegisterData): Promise<User> => {
    const { api } = await import('../lib/api');
    const res = await api.auth.register(data);
    setUser(res.user);
    return res.user;
  };

  const loginWithGoogle = async (credential: string): Promise<User> => {
    const { api } = await import('../lib/api');
    const data = await api.auth.google(credential);
    setUser(data.user);
    return data.user;
  };

  const logout = async (): Promise<void> => {
    const { api } = await import('../lib/api');
    try {
      await api.auth.logout();
    } catch (err) {
      // Même si le réseau échoue, la déconnexion locale doit avoir lieu :
      // le cookie restera invalide côté serveur à l'expiration.
      console.warn("Échec déconnexion serveur, déconnexion locale forcée:", err);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
